"use client";

import React, { useEffect, useState } from "react";
import { useAdministrationContext } from "./administration_context";
import { post } from "../../lib/api";

import { Checkbox } from "../../components/ui/checkbox";
import { Card, CardContent } from "../../components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../components/ui/table";
import { toast } from "sonner";
import LoadingScreenDenda from "../Unique/Animation/loading_screen";
import { InfoIcon } from "lucide-react";

export const components = [
  { name: "Label Table", status: "Discontinued", page: "All", sub_page: "Main" },
  { name: "Challenge Viewer", status: "Live", page: "All", sub_page: "Main" },
  { name: "Challenge Editor", status: "Discontinued", page: "All", sub_page: "Main" },
  { name: "Trivia Generator", status: "Live", page: "Trivia", sub_page: "Main" },
  { name: "Nonogram Generator", status: "Live", page: "Nonogram", sub_page: "Main" },
  { name: "Component Manager", status: "Live", page: "Administration", sub_page: "Main" },
  { name: "Page Manager", status: "Live", page: "Administration", sub_page: "Main" },
  { name: "User Manager", status: "Live", page: "Administration", sub_page: "Main" },
  { name: "Graph", status: "Live", page: "All", sub_page: "Statistics" },
  { name: "Numbers", status: "Live", page: "All", sub_page: "Statistics" },
  { name: "Feedback", status: "Live", page: "All", sub_page: "Statistics" },
  { name: "Nonogram Viewer", status: "Live", page: "Nonogram", sub_page: "Main" },
  { name: "File Uploader", status: "Live", page: "All", sub_page: "Main" },
  { name: "Structure Preview", status: "Live", page: "Combinations", sub_page: "Main" },
  { name: "Tile-set Preview", status: "Live", page: "Combinations", sub_page: "Main" },
  { name: "Existing Nonograms", status: "Live", page: "Nonogram", sub_page: "Main" },
  { name: "In-depth Statistics", status: "Live", page: "All", sub_page: "Statistics" },
  { name: "Users", status: "Live", page: "All", sub_page: "Statistics" },
  { name: "Completion Pie Chart", status: "Live", page: "All", sub_page: "Statistics" },
  { name: "Total Time Graph", status: "Live", page: "All", sub_page: "Statistics" },
];

const structureLiveComponents = (liveComponents) => {
  let result = [];
  if (Array.isArray(liveComponents)) {
    liveComponents.forEach((component) => {
      if (component[1] === "")
        result.push({
          name: component.component_id,
          page: component.page_id,
          sub_page: "Main",
        });
      else
        result.push({
          name: component.component_id,
          page: component.page_id,
          sub_page: component.parent_id,
        });
    });
  }
  return result;
};

const structurePages = (pages) => {
  let result = [];
  if (Array.isArray(pages)) {
    pages.forEach((page) => {
      // Check if the page already exists in the result array
      const isPageExist = result.some((entry) => entry.page === (page.parent_id || page.page_id) && entry.sub_page === (page.page_id || "Main"));
      // Only add the page if it doesn't already exist
      if (!isPageExist) {
        if (page.parent_id === "") {
          result.push({ page: page.page_id, sub_page: "Main" });
        } else {
          result.push({ page: page.parent_id, sub_page: page.page_id });
        }
      }
    });
  }
  return result;
};

const filterComponents = (page, sub_page, live_components) => {
  let converted_components = [];
  let converted_liveComponents = [];
  let filtered_components = [];

  components.forEach((component) => {
    if ((component["page"] === "All" || component["page"] === page) && (component["sub_page"] === "All" || component["sub_page"] === sub_page)) converted_components.push(component);
  });
  live_components.forEach((component) => {
    if ((component.sub_page === "" && component.page === page) || component.sub_page === page) converted_liveComponents.push(component);
  });

  converted_components.forEach((component) => {
    const liveComponent = converted_liveComponents.find((liveComponent) => liveComponent.name === component.name);
    const isLive = !!liveComponent;
    filtered_components.push({
      ...component,
      state: isLive,
    });
  });

  return filtered_components.sort((a, b) => {
    if (a.state && !b.state) return -1;
    if (!a.state && b.state) return 1;
    return 0;
  });
};

function getPostData(component, selectedPage, selectedSubPage) {
  let parent_id;
  let page_id;

  if (selectedSubPage === "Main") {
    parent_id = "";
    page_id = selectedPage;
  } else {
    parent_id = selectedPage;
    page_id = selectedSubPage;
  }
  return {
    method: component["state"] ? "removePageComponent" : "addPageComponent",
    name: component["name"],
    parent_id: parent_id,
    page_id: page_id,
  };
}

const ComponentManager = () => {
  const { user } = useAdministrationContext();
  const [pages, setPages] = useState([]);
  const [liveComponents, setLiveComponents] = useState([]);
  const [selectedPage, setSelectedPage] = useState("");
  const [selectedSubPage, setSelectedSubPage] = useState("");
  const [filteredComponents, setFilteredComponents] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const getPages = async () => {
      const pagesData = await post({ method: "getPagesSimple", user_id: user });
      setPages(structurePages(pagesData));

      // Auto-select the first page and sub-page
      const structuredPages = structurePages(pagesData);
      if (structuredPages.length > 0) {
        const firstPage = structuredPages[0].page;
        setSelectedPage(firstPage);

        const subPages = structuredPages.filter((p) => p.page === firstPage);
        if (subPages.length > 0) {
          setSelectedSubPage(subPages[0].sub_page);
        }
      }

      setLoading(false);
    };
    getPages();

    const getLiveComponents = async () => {
      const liveComponentsData = await post({
        method: "getComponentsWithOrder",
        user_id: user,
      });
      setLiveComponents(structureLiveComponents(liveComponentsData));
    };
    getLiveComponents();
  }, [user]);

  useEffect(() => {
    if (selectedPage && selectedSubPage) {
      setFilteredComponents(filterComponents(selectedPage, selectedSubPage, liveComponents));
    }
  }, [selectedPage, selectedSubPage, liveComponents]);

  const handlePageChange = (value) => {
    setSelectedPage(value);
    const subPages = pages.filter((p) => p.page === value).map((p) => p.sub_page);
    if (subPages.length > 0) setSelectedSubPage(subPages[0]);
  };

  const handleSubPageChange = (value) => {
    setSelectedSubPage(value);
  };

  const handleCheckboxChange = async (component) => {
    // Show a loading toast immediately
    const loadingToastId = toast.loading("Updating " + component.name + " component...");

    try {
      const postData = getPostData(component, selectedPage, selectedSubPage);
      const response = await post(postData);

      if (response.status === "error") {
        throw new Error("Failed to update component status");
      }

      // Update local state immediately
      const isCurrentlyLive = component.state;
      if (isCurrentlyLive) {
        setLiveComponents(liveComponents.filter((liveComponent) => liveComponent.name !== component.name));
        toast.success("Component deactivated", {
          description: `${component.name} has been removed from this page`,
        });
      } else {
        setLiveComponents([
          ...liveComponents,
          {
            name: component.name,
            page: selectedPage,
            sub_page: selectedSubPage,
            state: true,
          },
        ]);
        toast.success("Component activated", {
          description: `${component.name} has been added to this page`,
        });
      }
    } catch (error) {
      // Show an error toast
      toast.error("Error updating component", {
        description: "Failed to update component status",
      });
    } finally {
      // Always dismiss the loading toast
      toast.dismiss(loadingToastId);
    }
  };

  return (
    <div className="container mx-auto ">
      <div className="sticky top-0 z-10 pt-2 pb-4 border-b mb-6">
        <div className="flex flex-wrap gap-4 items-center justify-center">
          <Select value={selectedPage} onValueChange={handlePageChange}>
            <SelectTrigger className="w-36">
              <SelectValue placeholder="Select Page" />
            </SelectTrigger>
            <SelectContent>
              {[...new Set(pages.map((p) => p.page))].map((page, idx) => (
                <SelectItem key={idx} value={page}>
                  {page}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Select value={selectedSubPage} onValueChange={handleSubPageChange}>
            <SelectTrigger className="w-36">
              <SelectValue placeholder="Select Sub-Page" />
            </SelectTrigger>
            <SelectContent>
              {pages
                .filter((p) => p.page === selectedPage)
                .map((p, idx) => (
                  <SelectItem key={idx} value={p.sub_page}>
                    {p.sub_page}
                  </SelectItem>
                ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {loading ? (
        <LoadingScreenDenda text="Loading..." />
      ) : (
        <div className="mt-6">
          {filteredComponents.length > 0 ? (
            <Card className="shadow-md overflow-hidden dark:bg-[var(--header-color-dark)]">
              <CardContent className="p-0 overflow-hidden">
                <Table>
                  <TableHeader className="bg-muted/50 sticky bg-purple-700 text-white">
                    <TableRow>
                      <TableHead className="w-12 sticky">Active</TableHead>
                      <TableHead>Name</TableHead>
                      <TableHead>Status</TableHead>
                    </TableRow>
                  </TableHeader>

                  <TableBody>
                    {/* Active Components */}
                    {filteredComponents.filter((c) => c.state).length > 0 && (
                      <>
                        <TableRow className="bg-muted/10">
                          <TableCell colSpan={4} className="font-semibold py-2">
                            Active Components
                          </TableCell>
                        </TableRow>
                        {filteredComponents
                          .filter((component) => component.state)
                          .map((component, index) => (
                            <TableRow key={component.name} className="hover:bg-muted transition-all border-b">
                              <TableCell className="w-12">
                                <Checkbox
                                  className="data-[state=checked]:bg-purple-700 dark:data-[state=checked]:bg-purple-700 shadow-none text-white"
                                  checked={component.state}
                                  onCheckedChange={() => handleCheckboxChange(component)}
                                />
                              </TableCell>
                              <TableCell>{component.name}</TableCell>
                              <TableCell>{component.status}</TableCell>
                            </TableRow>
                          ))}
                      </>
                    )}

                    {/* Inactive Components */}
                    {filteredComponents.filter((c) => !c.state).length > 0 && (
                      <>
                        <TableRow className="bg-muted/10">
                          <TableCell colSpan={4} className="font-semibold py-2">
                            Inactive Components
                          </TableCell>
                        </TableRow>
                        {filteredComponents
                          .filter((component) => !component.state)
                          .map((component, index) => (
                            <TableRow key={component.name} className="hover:bg-muted transition-all border-b">
                              <TableCell className="w-12">
                                <Checkbox className="shadow-none" checked={component.state} onCheckedChange={() => handleCheckboxChange(component)} />
                              </TableCell>
                              <TableCell>{component.name}</TableCell>
                              <TableCell>{component.status}</TableCell>
                            </TableRow>
                          ))}
                      </>
                    )}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          ) : (
            <Card className="p-8 text-center">
              <p className="text-muted-foreground">No components available for this page and sub-page.</p>
            </Card>
          )}
        </div>
      )}
      <div className="text-sm text-gray-500 flex items-center gap-1 pt-2">
        <InfoIcon className="h-4 w-4" />
        By clicking the checkbox you can choose which components are visible for each page and sub-page.
      </div>
    </div>
  );
};

export default ComponentManager;
